import React, { useState } from "react";
import {
  TextInput,
  Stack,
  Select,
  NumberInput,
  Checkbox,
  ColorInput,
  Collapse,
  Group,
  SegmentedControl,
  Text,
  Tooltip,
  Switch,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useSalon } from "../../../context/SalonContext";
import { SalonContextType } from "../../../types/context";
import ServiceDescriptionEditor from "../../common/ServiceDescriptionEditor";
import WeekdayToggle from "./WeekdayToggle";
import { DayAllowedFlags, extractDayFlags } from "./dayAvailability";
import styles from "./ServiceInformationStep.module.css";

interface ServiceInformationStepProps {
  formData: any;
  setFormData: (data: any) => void;
  errors: Record<string, string>;
  serviceTypes: any[] | null;
  isMobile: boolean;
  /**
   * Med-spa only: shows the "Pricing basis" control (per service /
   * per syringe / per unit). Non-med-spa salons are forced to
   * per_service server-side, so the control stays hidden for them.
   */
  showPricingBasis?: boolean;
}

function generateDurationOptions() {
  const options = [];
  for (let minutes = 5; minutes <= 240; minutes += 5) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const label =
      hours > 0
        ? `${hours} hr${hours > 1 ? "s" : ""}${mins > 0 ? ` ${mins} min` : ""}`
        : `${mins} min`;
    options.push({ value: `${minutes}`, label });
  }
  return options;
}

export default function ServiceInformationStep({
  formData,
  setFormData,
  errors,
  serviceTypes,
  isMobile,
  showPricingBasis = false,
}: ServiceInformationStepProps) {
  const { businessInfo } = useSalon() as SalonContextType;
  const [showNewServiceType, setShowNewServiceType] = useState(false);
  const [newServiceType, setNewServiceType] = useState("");

  const priceBasis = formData.price_basis ?? "per_service";
  const priceLabel =
    priceBasis === "per_syringe"
      ? "Price per syringe"
      : priceBasis === "per_unit"
        ? "Price per unit"
        : "Price";

  const showFaronPrivateRoom =
    businessInfo?.id === process.env.REACT_APP_FARON_SALON_UUID;

  const canEnableCoF =
    !!businessInfo?.stripe_connect_account_id &&
    (businessInfo?.stripe_connect_account_is_onboarded === true ||
      businessInfo?.stripe_connect_account_is_onboarded === "true");

  const defaultServiceTypes = [
    { value: "Hair", label: "Hair" },
    { value: "Color", label: "Color" },
  ];

  const serviceTypesOptions = [
    ...(serviceTypes || defaultServiceTypes),
    { value: "new", label: "+ Add New" },
  ];

  // Setting the salon-level available days defaults that selection across every
  // staff member; each staff can still be overridden in the next step.
  const handleAvailableDaysChange = (flags: DayAllowedFlags) => {
    const updatedEmployeeSettings = Object.entries(
      formData.employeeSettings || {},
    ).reduce((acc: any, [employeeId, settings]: any) => {
      acc[employeeId] = { ...settings, ...flags };
      return acc;
    }, {});

    setFormData({
      ...formData,
      ...flags,
      employeeSettings: updatedEmployeeSettings,
    });
  };

  const defaultColorSwatches = [
    "#3174ad",
    "#ffe06e",
    "#f7a725",
    "#fa5252",
    "#e64980",
    "#be4bdb",
    "#7950f2",
    "#4c6ef5",
    "#228be6",
    "#15aabf",
    "#12b886",
    "#40c057",
  ];

  return (
    <div className={styles.container}>
      <Stack gap={isMobile ? "sm" : "md"} className={styles.formSection}>
        <TextInput
          label="Service Name"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          error={errors.name}
          size={isMobile ? "sm" : "md"}
          placeholder="Service Name"
          maxLength={256}
        />

        <Select
          label={
            <Group gap={4}>
              <span>Service Type</span>
              <Text span c="red">
                *
              </Text>
              <Tooltip
                label="Used to group and organize services for your customers. (Ex: Haircut, Color, Manicure, Waxing)"
                position="right-start"
                multiline
                w={350}
              >
                <IconInfoCircle
                  size={16}
                  style={{ display: "inline-block", cursor: "help" }}
                />
              </Tooltip>
            </Group>
          }
          value={showNewServiceType ? "new" : formData.type}
          onChange={(value) => {
            if (value === "new") {
              setShowNewServiceType(true);
              setFormData({ ...formData, type: "" });
            } else {
              setShowNewServiceType(false);
              setFormData({ ...formData, type: value });
            }
          }}
          data={serviceTypesOptions}
          error={errors.type}
          size={isMobile ? "sm" : "md"}
          placeholder="Service Type"
        />

        <Collapse in={showNewServiceType}>
          <TextInput
            label={
              <Group gap={4}>
                <span>New Service Type</span>
                <Text span c="red">
                  *
                </Text>
              </Group>
            }
            value={newServiceType}
            onChange={(e) => {
              const value = e.target.value.slice(0, 128);
              setNewServiceType(value);
              setFormData({ ...formData, type: value });
            }}
            placeholder="Enter new service type"
            maxLength={128}
            error={errors.type}
            size={isMobile ? "sm" : "md"}
          />
        </Collapse>

        <ServiceDescriptionEditor
          label="Description"
          value={formData.description}
          onChange={(html) => setFormData({ ...formData, description: html })}
          error={errors.description}
          placeholder="Service Description"
        />

        <Select
          label={
            <Group gap={4} align="normal">
              <span>Base Duration</span>
              <Text span c="red">
                *
              </Text>
              <Tooltip
                label="Active time spent on service initially"
                position="right-start"
                multiline
                w={300}
              >
                <IconInfoCircle
                  size={16}
                  style={{
                    display: "inline-block",
                    cursor: "help",
                    verticalAlign: "middle",
                  }}
                />
              </Tooltip>
            </Group>
          }
          value={formData.duration}
          onChange={(value) => setFormData({ ...formData, duration: value })}
          data={generateDurationOptions()}
          error={errors.duration}
          size={isMobile ? "sm" : "md"}
        />

        <Select
          label={
            <Group gap={4} align="normal">
              <span>Processing Time</span>
              <Tooltip
                label="Inactive time for treatments/products to process (e.g., color development)"
                position="right-start"
                multiline
                w={300}
              >
                <IconInfoCircle
                  size={16}
                  style={{
                    display: "inline-block",
                    cursor: "help",
                    verticalAlign: "middle",
                  }}
                />
              </Tooltip>
            </Group>
          }
          value={formData.processingTime ?? "0"}
          onChange={(value) =>
            setFormData({ ...formData, processingTime: value })
          }
          data={[{ value: "0", label: "None" }, ...generateDurationOptions()]}
          size={isMobile ? "sm" : "md"}
        />

        <Select
          label={
            <Group gap={4} align="normal">
              <span>Finishing Time</span>
              <Tooltip
                label="Active time to finalize the service after processing"
                position="right-start"
                multiline
                w={300}
              >
                <IconInfoCircle
                  size={16}
                  style={{
                    display: "inline-block",
                    cursor: "help",
                    verticalAlign: "middle",
                  }}
                />
              </Tooltip>
            </Group>
          }
          value={formData.finishingTime ?? "0"}
          onChange={(value) =>
            setFormData({ ...formData, finishingTime: value })
          }
          data={[{ value: "0", label: "None" }, ...generateDurationOptions()]}
          size={isMobile ? "sm" : "md"}
        />

        <Select
          label={
            <Group gap={4} align="normal">
              <span>Buffer Time</span>
              <Tooltip
                label="Time blocked before next appointment begins (ex: cleanup, breaks)"
                position="right-start"
                multiline
                w={300}
              >
                <IconInfoCircle
                  size={16}
                  style={{
                    display: "inline-block",
                    cursor: "help",
                    verticalAlign: "middle",
                  }}
                />
              </Tooltip>
            </Group>
          }
          value={formData.bufferTime ?? "0"}
          onChange={(value) => setFormData({ ...formData, bufferTime: value })}
          data={[{ value: "0", label: "None" }, ...generateDurationOptions()]}
          size={isMobile ? "sm" : "md"}
        />

        <Group gap={4} align="flex-start">
          <Checkbox
            label="Enable Double Booking (Recommended)"
            checked={formData.enableDoubleBooking || false}
            onChange={(event) =>
              setFormData({
                ...formData,
                enableDoubleBooking: event.currentTarget.checked,
              })
            }
            size={isMobile ? "sm" : "md"}
          />
          <Tooltip
            label="When enabled, processing time can be booked for other services"
            position="right"
            multiline
            w={250}
          >
            <IconInfoCircle
              size={16}
              style={{ display: "inline-block", cursor: "help" }}
            />
          </Tooltip>
        </Group>

        {showPricingBasis && (
          <Stack gap={4}>
            <Group gap={4}>
              <Text size="sm" fw={500}>
                Pricing basis
              </Text>
              <Tooltip
                label="Per-syringe / per-unit services are billed by quantity used — staff confirm the units at checkout and the total recomputes automatically."
                position="right"
                multiline
                w={280}
              >
                <IconInfoCircle
                  size={16}
                  style={{ display: "inline-block", cursor: "help" }}
                />
              </Tooltip>
            </Group>
            <SegmentedControl
              value={priceBasis}
              onChange={(value) =>
                setFormData({ ...formData, price_basis: value })
              }
              data={[
                { label: "Per service", value: "per_service" },
                { label: "Per syringe", value: "per_syringe" },
                { label: "Per unit", value: "per_unit" },
              ]}
              size={isMobile ? "xs" : "sm"}
            />
          </Stack>
        )}

        {canEnableCoF ? (
          <Group grow className={styles.calendarColorGroup}>
            <NumberInput
              label={priceLabel}
              required
              value={formData.price}
              onChange={(value) => setFormData({ ...formData, price: value })}
              placeholder="Ex: $20, $30"
              min={0}
              decimalScale={2}
              fixedDecimalScale
              prefix="$"
              error={errors.price}
              className={styles.calendarColorInput}
              size={isMobile ? "sm" : "md"}
            />
            <div className={styles.onlineBookingContainer}>
              <div className={styles.onlineBookingLabel}>
                <Text size="sm" fw={500}>
                  Require card on file to book
                </Text>
                <Tooltip
                  label="When enabled, customers must enter and save credit card details before booking this service. Requires the business to be successfully onboarded to Stripe Payments."
                  position="right"
                  multiline
                  w={200}
                >
                  <IconInfoCircle
                    size={16}
                    style={{ display: "inline-block", cursor: "help" }}
                  />
                </Tooltip>
              </div>
              <Switch
                checked={formData.require_card_to_book ?? false}
                onChange={(event) =>
                  setFormData({
                    ...formData,
                    require_card_to_book: event.currentTarget.checked,
                  })
                }
                size={isMobile ? "sm" : "md"}
              />
            </div>
          </Group>
        ) : (
          <NumberInput
            label={priceLabel}
            required
            value={formData.price}
            onChange={(value) => setFormData({ ...formData, price: value })}
            placeholder="Ex: $20, $30"
            min={0}
            decimalScale={2}
            fixedDecimalScale
            prefix="$"
            error={errors.price}
            size={isMobile ? "sm" : "md"}
          />
        )}

        <Checkbox
          label="Starts At"
          checked={formData.startsAt}
          onChange={(event) =>
            setFormData({ ...formData, startsAt: event.currentTarget.checked })
          }
          size={isMobile ? "sm" : "md"}
        />

        <Group grow className={styles.calendarColorGroup}>
          <ColorInput
            label="Default Calendar Color"
            value={formData.default_calendar_event_color}
            onChange={(value) =>
              setFormData({ ...formData, default_calendar_event_color: value })
            }
            error={errors.default_calendar_event_color}
            format="hex"
            swatches={defaultColorSwatches}
            withEyeDropper={false}
            className={styles.calendarColorInput}
            size={isMobile ? "sm" : "md"}
          />

          <div className={styles.onlineBookingContainer}>
            <div className={styles.onlineBookingLabel}>
              <Text size="sm" fw={500}>
                Show in online booking
              </Text>
              <Tooltip
                label="Enable this setting to let clients book online (disable for internal use only)"
                position="right"
                multiline
                w={200}
              >
                <IconInfoCircle
                  size={16}
                  style={{ display: "inline-block", cursor: "help" }}
                />
              </Tooltip>
            </div>
            <Switch
              checked={formData.is_self_servicable}
              onChange={(event) =>
                setFormData({
                  ...formData,
                  is_self_servicable: event.currentTarget.checked,
                })
              }
              size={isMobile ? "sm" : "md"}
            />
          </div>
        </Group>

        <WeekdayToggle
          label="Available days"
          description="Days this service can be booked. This applies to all staff by default; you can override individual staff in the next step."
          tooltip="Clear a day to stop this service from being booked on that day. New services are available every day by default."
          value={extractDayFlags(formData)}
          onChange={handleAvailableDaysChange}
          size={isMobile ? "sm" : "md"}
        />

        {showFaronPrivateRoom && (
          <Checkbox
            label="Private Room"
            checked={formData.faron__is_private_room}
            onChange={(event) =>
              setFormData({
                ...formData,
                faron__is_private_room: event.currentTarget.checked,
              })
            }
            size={isMobile ? "sm" : "md"}
          />
        )}
      </Stack>
    </div>
  );
}
